import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { notify } from "../../components/ui/notify";
import {
  Typography,
  Space,
  Button,
  Card,
  Modal,
  Spin,
  Empty,
  Tag,
  Row,
  Col,
  Input,
  Divider,
} from "antd";
import {
  DeleteOutlined,
  ShopOutlined,
  PlusOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { PATH } from "../../redux/utils";
import {
  actionCorresponsalGetById,
  actionCorresponsalUpdate,
  actionCorresponsalDelete,
} from "../../redux/actions/corresponsales/corresponsales";
import { DataBadge } from "../../components/ui";
import { usePermisos } from "../../context/PermisosContext";
import { STATUS_PILL_HEX } from "./materias/laboral/tribunalEstatus.js";
import "../../assets/css/LaboralExpedienteDetallePage.css";
import "../../components/conciliacion/detalles/LaboralExpedienteDetallePage.css";
import "./materias/laboral/LaboralProcedimientoPage.css";
import "../../components/tribunal/TribunalPage.css";

dayjs.locale("es");

const { Text, Title } = Typography;

const fmtDateLong = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
};

// Mismo patrón "label arriba / valor abajo" que usa la tarjeta "Datos del
// expediente" de Tribunal/Junta (laboral-exp-field-grid).
function Field({ label, value }) {
  return (
    <div className="laboral-exp-field">
      <span className="laboral-exp-field-label">{label}</span>
      <span className="laboral-exp-field-value">{value ?? "—"}</span>
    </div>
  );
}

const toTitleCase = (str) => {
  if (!str || typeof str !== "string") return "—";
  return str
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const authHeader = () => {
  const token = localStorage.getItem("tokenadmin") || localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const api = axios.create({
  baseURL: PATH,
  headers: { "Content-Type": "application/json" },
});

// ===================== Actividad Tab =====================
function ActividadTab({ actividad, loading }) {
  const fmtDt = (d) => {
    if (!d) return "—";
    const x = dayjs(d);
    return x.isValid() ? x.format("DD/MM/YYYY, HH:mm") : "—";
  };

  const getActionMeta = (action) => {
    const a = String(action || "").toUpperCase();
    if (a.includes("CREATE") || a.includes("INSERT")) {
      return { label: "Registro", tagColor: "success", dot: "#52c41a" };
    }
    if (a.includes("UPDATE") || a.includes("EDIT") || a.includes("PATCH")) {
      return { label: "Actualización", tagColor: "processing", dot: "#1677ff" };
    }
    if (a.includes("DELETE")) {
      return { label: "Eliminación", tagColor: "error", dot: "#ff4d4f" };
    }
    return { label: toTitleCase(action || "Acción"), tagColor: "default", dot: "#8c8c8c" };
  };

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Card style={{ borderRadius: 12, marginTop: 4 }} bodyStyle={{ padding: "20px 24px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
        <Text strong style={{ fontSize: 17, color: "#111827" }}>
          Actividad del corresponsal
        </Text>
        <div
          style={{
            background: "#f0f0f0",
            borderRadius: 20,
            padding: "2px 11px",
            fontSize: 13,
            fontWeight: 600,
            color: "#4b5563",
          }}
        >
          {actividad.length}
        </div>
      </div>

      {actividad.length === 0 ? (
        <Empty description="Sin actividad registrada para este corresponsal" />
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {actividad.map((item, idx) => {
            const { label, tagColor, dot } = getActionMeta(item.action);
            const isLast = idx === actividad.length - 1;
            return (
              <div key={item.id_audit_log ?? idx} style={{ display: "flex", gap: 14 }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    paddingTop: 17,
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: 11,
                      height: 11,
                      borderRadius: "50%",
                      background: dot,
                      boxShadow: `0 0 0 3px ${dot}22`,
                      flexShrink: 0,
                    }}
                  />
                  {!isLast && (
                    <div
                      style={{
                        width: 2,
                        flex: 1,
                        background: "#e5e7eb",
                        minHeight: 16,
                        marginTop: 4,
                      }}
                    />
                  )}
                </div>
                <div style={{ flex: 1, paddingBottom: isLast ? 0 : 10 }}>
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid #f3f4f6",
                      borderRadius: 10,
                      padding: "14px 16px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
                      <Tag color={tagColor} style={{ margin: 0, fontWeight: 600, flexShrink: 0, fontSize: 12 }}>
                        {label}
                      </Tag>
                      <Text style={{ fontSize: 14, lineHeight: 1.45, color: "#1f2937" }}>
                        {item.message || "—"}
                      </Text>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 8 }}>
                      <Text style={{ fontSize: 12, color: "#9ca3af" }}>{fmtDt(item.datetime)}</Text>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                          {item.user_name || "—"}
                        </div>
                        {item.user_email && (
                          <div style={{ fontSize: 12, color: "#9ca3af" }}>{item.user_email}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ===================== Empresas Tab =====================
function EmpresasTab({ idCorresponsal }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [removing, setRemoving] = useState(null);

  const id = Number(idCorresponsal);

  const fetchEmpresas = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("empresas", { headers: authHeader() });
      const list = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];

      // Fetch full details in parallel — el endpoint lista no incluye corresponsales anidados
      const details = await Promise.all(
        list.map((e) =>
          api
            .get(`empresas/${e.id_empresa || e.id}`, { headers: authHeader() })
            .then((r) => r.data)
            .catch(() => e)
        )
      );

      const matched = [];
      for (const emp of details) {
        const razones = Array.isArray(emp.razones_sociales) ? emp.razones_sociales : [];
        for (const rs of razones) {
          const corrs = Array.isArray(rs.corresponsales) ? rs.corresponsales : [];
          const found = corrs.some((c) => (c.id_corresponsal || c.id) === id);
          if (found) matched.push({ empresa: emp, razonSocial: rs });
        }
      }
      setItems(matched);
    } catch {
      notify.error({ message: "Error", description: "No se pudieron cargar las empresas." });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchEmpresas(); }, [fetchEmpresas]);

  const buildUpdatedRS = (empData, razonNombre) =>
    (empData.razones_sociales || []).map((rs) => {
      const nombre = rs.nombre || rs.code || "";
      const currentIds = (Array.isArray(rs.corresponsales) ? rs.corresponsales : [])
        .map((c) => c.id || c.id_corresponsal)
        .filter(Boolean);
      if (nombre === razonNombre) {
        return { nombre, corresponsales_ids: currentIds.filter((cid) => cid !== id) };
      }
      return { nombre, corresponsales_ids: currentIds };
    });

  const handleRemove = (empresaId, razonNombre) => {
    Modal.confirm({
      title: "Quitar razón social",
      content: `¿Desea quitar "${razonNombre}" de este corresponsal?`,
      okText: "Quitar",
      okType: "danger",
      cancelText: "Cancelar",
      centered: true,
      onOk: async () => {
        const key = `${empresaId}-${razonNombre}`;
        try {
          setRemoving(key);
          const { data: empData } = await api.get(`empresas/${empresaId}`, { headers: authHeader() });
          const updatedRS = buildUpdatedRS(empData, razonNombre);
          await api.patch(`empresas/${empresaId}`, { razones_sociales: updatedRS }, { headers: authHeader() });
          notify.success({ message: "Razón social quitada del corresponsal" });
          fetchEmpresas();
        } catch (e) {
          notify.error({ message: "Error", description: e?.response?.data?.detail || "No se pudo quitar la razón social." });
        } finally {
          setRemoving(null);
        }
      },
    });
  };

  const grouped = useMemo(() => {
    const map = new Map();
    for (const { empresa, razonSocial } of items) {
      const key = empresa.id_empresa || empresa.id;
      if (!map.has(key)) map.set(key, { empresa, razonesSociales: [] });
      map.get(key).razonesSociales.push(razonSocial);
    }
    return Array.from(map.values());
  }, [items]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16, marginBottom: 16 }}>
        <Text style={{ color: "#64748b", fontSize: 13 }}>
          <span style={{ fontWeight: 600, color: "#0f172a" }}>{items.length}</span>{" "}
          razón{items.length !== 1 ? "es" : ""} social{items.length !== 1 ? "es" : ""} asignada{items.length !== 1 ? "s" : ""}
        </Text>
        <Button
          className="custom-button"
          icon={<PlusOutlined />}
          onClick={() =>
            navigate("/empresas/crear", {
              state: {
                preCorresponsalId: id,
                returnUrl: `/corresponsales/${idCorresponsal}`,
                returnTab: "empresas",
              },
            })
          }
          style={{ borderRadius: 8, fontWeight: 500, width: "auto", flexShrink: 0 }}
        >
          Agregar empresa
        </Button>
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: "center" }}>
          <Spin size="large" />
        </div>
      ) : grouped.length === 0 ? (
        <Card style={{ borderRadius: 12 }}>
          <Empty description="Este corresponsal no tiene razones sociales asignadas" />
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {grouped.map(({ empresa, razonesSociales }) => {
            const empId = empresa.id_empresa || empresa.id;
            return (
              <Card
                key={empId}
                style={{ borderRadius: 12, border: "1px solid #e2e8f0" }}
                bodyStyle={{ padding: "16px 20px" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: "linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#fff", fontSize: 16, flexShrink: 0,
                  }}>
                    <ShopOutlined />
                  </div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
                      {empresa.nombre || "—"}
                    </div>
                    {empresa.correo && (
                      <div style={{ fontSize: 12, color: "#64748b" }}>{empresa.correo}</div>
                    )}
                  </div>
                </div>
                <Divider style={{ margin: "8px 0 12px" }} />
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {razonesSociales.map((rs) => {
                    const rsNombre = rs.nombre || rs.code || "—";
                    const itemKey = `${empId}-${rsNombre}`;
                    return (
                      <div
                        key={itemKey}
                        style={{
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          padding: "8px 12px", background: "#f8fafc", borderRadius: 8,
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <Space size={8}>
                          <FileTextOutlined style={{ color: "#475569" }} />
                          <Text style={{ fontSize: 14, color: "#1e293b" }}>{rsNombre}</Text>
                        </Space>
                        <Button
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          loading={removing === itemKey}
                          onClick={() => handleRemove(empId, rsNombre)}
                          style={{ borderRadius: 6 }}
                        >
                          Quitar
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ===================== Main Page =====================
export default function CorresponsalDetallePage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { idCorresponsal } = useParams();
  const location = useLocation();

  const { perm } = usePermisos() || { perm: () => true };
  const canEditar = perm("corresponsales", "editar");
  const canEliminar = perm("corresponsales", "eliminar");

  const [detalle, setDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState("info");

  const [actividad, setActividad] = useState([]);
  const [loadingAct, setLoadingAct] = useState(false);
  const [loadedAct, setLoadedAct] = useState(false);

  // Edición inline de "Datos del corresponsal" (mismo patrón que Tribunal/Junta)
  const [editingDatos, setEditingDatos] = useState(false);
  const [editingForm, setEditingForm] = useState({});
  const [savingDatos, setSavingDatos] = useState(false);

  const loadDetalle = useCallback(async () => {
    try {
      setLoadingDetalle(true);
      const data = await dispatch(actionCorresponsalGetById(idCorresponsal));
      setDetalle(data || null);
    } catch {
      notify.error({
        message: "Error al cargar",
        description: "No se pudo obtener el detalle del corresponsal.",
      });
      setDetalle(null);
    } finally {
      setLoadingDetalle(false);
    }
  }, [dispatch, idCorresponsal]);

  // Cargar corresponsal
  useEffect(() => {
    loadDetalle();
  }, [loadDetalle]);

  const startEditDatos = () => {
    setEditingForm({
      nombre: detalle?.nombre || "",
      correo: detalle?.correo || "",
      celular: detalle?.celular || "",
    });
    setEditingDatos(true);
  };

  const handleSaveDatos = async () => {
    const nombre = (editingForm.nombre || "").trim();
    if (!nombre) {
      notify.error({ message: "Falta el nombre del corresponsal", placement: "bottomRight" });
      return;
    }
    setSavingDatos(true);
    try {
      const idToUpdate = detalle?.id_corresponsal ?? detalle?.id;
      const payload = {
        nombre,
        correo: (editingForm.correo || "").trim() || null,
        celular: (editingForm.celular || "").replace(/\D/g, "").slice(0, 15) || null,
      };
      await dispatch(actionCorresponsalUpdate(idToUpdate, payload, () => {}));
      await loadDetalle();
      setEditingDatos(false);
      notify.success({
        message: "Corresponsal actualizado",
        description: "Los cambios se guardaron correctamente.",
        placement: "bottomRight",
      });
    } catch (e) {
      notify.error({
        message: "No se pudo guardar",
        description: e?.response?.data?.detail || e?.message || "No se pudieron guardar los cambios.",
        placement: "bottomRight",
      });
    } finally {
      setSavingDatos(false);
    }
  };

  // Cargar actividad (lazy)
  const loadActividad = useCallback(async () => {
    if (loadedAct) return;
    try {
      setLoadingAct(true);
      const { data } = await api.get(`corresponsales/${idCorresponsal}/historial`, {
        headers: authHeader(),
        params: { limit: 200 },
      });
      const items = Array.isArray(data?.items) ? data.items : [];
      setActividad(items);
    } catch {
      notify.error({ message: "Error", description: "No se pudo cargar la actividad." });
    } finally {
      setLoadingAct(false);
      setLoadedAct(true);
    }
  }, [idCorresponsal, loadedAct]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === "actividad") loadActividad();
  };

  const handleDelete = () => {
    const idToDelete = detalle?.id_corresponsal ?? detalle?.id;
    if (!idToDelete) {
      notify.error({ message: "No se puede eliminar", description: "No se encontró el ID del corresponsal." });
      return;
    }
    Modal.confirm({
      title: "Eliminar corresponsal",
      content: "Esta acción elimina el corresponsal y su información relacionada.",
      okText: "Eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      centered: true,
      okButtonProps: { loading: deleting, disabled: deleting },
      cancelButtonProps: { disabled: deleting },
      onOk: async () => {
        try {
          setDeleting(true);
          await dispatch(
            actionCorresponsalDelete(idToDelete, () => {
              notify.success({ message: "Corresponsal eliminado", description: "Se eliminó correctamente." });
              navigate("/corresponsales");
            })
          );
        } catch (error) {
          const msg = error?.response?.data?.detail || error?.message || "No se pudo eliminar el corresponsal.";
          notify.error({ message: "Error al eliminar", description: msg });
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  if (loadingDetalle || !detalle) {
    return (
      <div className="lxp-root">
        <div className="lxp-wrap">
          <Card className="laboral-exp-summary-card p-2" style={{ marginTop: 16 }}>
            {loadingDetalle ? (
              <div style={{ padding: 40, textAlign: "center" }}>
                <Spin size="large" />
              </div>
            ) : (
              <Text type="secondary">Sin datos del corresponsal.</Text>
            )}
          </Card>
        </div>
      </div>
    );
  }

  const corresponsalNombre = detalle?.nombre || "corresponsal";
  const isActive = Number(detalle?.active ?? 1) === 1;
  const estatusPill = isActive
    ? { label: "ACTIVO", kind: "primary" }
    : { label: "INACTIVO", kind: "gray" };

  const TABS = [
    { key: "info", label: "Info", icon: <i className="fa-solid fa-circle-info" /> },
    { key: "empresas", label: "Empresas", icon: <i className="fa-solid fa-building" /> },
    { key: "actividad", label: "Actividad", icon: <i className="fa-solid fa-bolt" /> },
  ];

  return (
    <div className="lxp-root">
      <div className="lxp-wrap">
        {/* SUMMARY CARD — mismas clases laboral-exp-* que el header de Tribunal/Junta */}
        <div className="laboral-exp-main" style={{ background: "transparent", padding: 0, minHeight: "auto" }}>
          <Card className="laboral-exp-summary-card p-2">
            <Row align="middle" gutter={[16, 16]} justify="space-between" wrap>
              <Col flex="auto">
                <Space direction="vertical" size={8}>
                  <div className="laboral-exp-summary-title">
                    <Space align="center" wrap>
                      <div className="laboral-exp-icon-badge">
                        <i className="fa-solid fa-user-tie" />
                      </div>
                      <Title
                        level={4}
                        style={{ margin: 0, fontSize: 28, fontWeight: 650, letterSpacing: "-0.01em", lineHeight: 1.2, color: "#0f172a" }}
                      >
                        {toTitleCase(corresponsalNombre).toUpperCase()}
                      </Title>
                      <DataBadge
                        color={STATUS_PILL_HEX[estatusPill.kind] || estatusPill.kind}
                        className="d-inline-flex align-items-center gap-1 status-pill-badge"
                      >
                        {estatusPill.label}
                      </DataBadge>
                    </Space>
                  </div>

                  <Space size="large" wrap className="laboral-exp-summary-meta">
                    <Space className="laboral-exp-meta-item">
                      <i className="fa-solid fa-calendar-days" />
                      <Text>Creado: {fmtDateLong(detalle.created_at)}</Text>
                    </Space>
                    <Space className="laboral-exp-meta-item">
                      <i className="fa-solid fa-address-card" />
                      <Text>Creado por: {toTitleCase(detalle.nombre_creado_por)}</Text>
                    </Space>
                  </Space>
                </Space>
              </Col>

              <Col flex="none">
                <Space>
                  {canEliminar && (
                  <Button
                    danger
                    type="primary"
                    icon={<i className="fa-solid fa-trash" />}
                    style={{ borderRadius: 8 }}
                    onClick={handleDelete}
                    loading={deleting}
                    disabled={deleting}
                  >
                    Eliminar
                  </Button>
                  )}
                </Space>
              </Col>
            </Row>

            {/* TABS BAR */}
            <div className="laboral-exp-header-tabs">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`laboral-exp-tab-btn ${activeTab === t.key ? "laboral-exp-tab-btn-active" : ""}`}
                  onClick={() => handleTabChange(t.key)}
                >
                  {t.icon}
                  {t.label}
                </button>
              ))}
            </div>
          </Card>
        </div>

        {/* TAB: INFO */}
        {activeTab === "info" && (
          <Card className="laboral-exp-detail-card laboral-exp-detail-card-main p-2" style={{ marginTop: 18 }}>
            <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div className="laboral-exp-icon-badge">
                  <i className="fa-solid fa-id-card" />
                </div>
                <h3 className="laboral-exp-card-header-title">Datos del corresponsal</h3>
              </div>

              {canEditar && !editingDatos && (
                <Button onClick={startEditDatos} className="laboral-exp-btn-clean">
                  Editar
                </Button>
              )}
            </div>

            {editingDatos ? (
              <div className="laboral-exp-edit-form">
                <div className="laboral-exp-edit-grid">
                  <div className="laboral-exp-edit-field">
                    <span className="laboral-exp-edit-label">Nombre</span>
                    <Input
                      value={editingForm.nombre}
                      onChange={(e) => setEditingForm((f) => ({ ...f, nombre: e.target.value }))}
                    />
                  </div>
                  <div className="laboral-exp-edit-field">
                    <span className="laboral-exp-edit-label">Correo</span>
                    <Input
                      value={editingForm.correo}
                      onChange={(e) => setEditingForm((f) => ({ ...f, correo: e.target.value }))}
                    />
                  </div>
                  <div className="laboral-exp-edit-field">
                    <span className="laboral-exp-edit-label">Celular</span>
                    <Input
                      value={editingForm.celular}
                      onChange={(e) =>
                        setEditingForm((f) => ({ ...f, celular: e.target.value.replace(/\D/g, "").slice(0, 15) }))
                      }
                    />
                  </div>
                </div>

                <div className="laboral-exp-edit-actions">
                  <Button onClick={() => setEditingDatos(false)} className="laboral-exp-btn-clean">
                    Cancelar
                  </Button>
                  <Button
                    type="primary"
                    loading={savingDatos}
                    onClick={handleSaveDatos}
                    className="laboral-exp-btn-save"
                    icon={<i className="fa-solid fa-floppy-disk" />}
                  >
                    Guardar
                  </Button>
                </div>
              </div>
            ) : (
              <div className="laboral-exp-field-grid">
                <Field label="Nombre" value={toTitleCase(detalle?.nombre)} />
                <Field label="Estatus" value={isActive ? "Activo" : "Inactivo"} />
                <Field label="Correo" value={detalle?.correo || "—"} />
                <Field label="Celular" value={detalle?.celular || "—"} />
              </div>
            )}
          </Card>
        )}

        {/* TAB: EMPRESAS */}
        {activeTab === "empresas" && (
          <Card className="laboral-exp-detail-card p-2" style={{ marginTop: 18 }}>
            <EmpresasTab idCorresponsal={idCorresponsal} />
          </Card>
        )}

        {/* TAB: ACTIVIDAD */}
        {activeTab === "actividad" && (
          <div style={{ marginTop: 18 }}>
            <ActividadTab actividad={actividad} loading={loadingAct} />
          </div>
        )}
      </div>
    </div>
  );
}
